import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Clock, LogIn, LogOut, Calendar, Timer, DollarSign, Coffee, FileText, AlertTriangle, Link2, User as UserIcon, MapPin, CalendarDays, Download,
  ChevronDown, ChevronRight, Briefcase, PartyPopper, Inbox, Menu, X, ArrowLeftRight,
  Trophy, Target, MessagesSquare,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import EmployeeCalendarView from '../components/EmployeeCalendarView';
import useMessageStream from '../hooks/useMessageStream';
import useChatStream from '../hooks/useChatStream';
import { ChatThreadList } from '../components/chat/ChatThreadList';
import { ChatConversation } from '../components/chat/ChatConversation';
import { RequestSwapDialog, MyShiftSwapsList } from '../components/ShiftSwapWidgets';
import { TeamChat } from '../components/chat/TeamChat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPLOYMENT_LABELS = { full_time: 'Full-time', part_time: 'Part-time', casual: 'Casual / On-call' };
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// "YOUR PROGRESS" banner shown to employees who have NOT yet hit the
// Employee-of-the-Month thresholds. Surfaces how many conversions / repeat
// bookings are left + a tiny encouragement message and (if known) the name
// of the current month's winner.
const EmployeeProgressBanner = ({ performance }) => {
  if (!performance) return null;
  const minConversions = performance.thresholds?.conversions ?? 10;
  const minBookings = performance.thresholds?.bookings ?? 5;
  const conversions = performance.monthly_conversions ?? 0;
  const bookings = performance.monthly_bookings ?? 0;
  const conversionsNeeded = Math.max(0, minConversions - conversions);
  const bookingsNeeded = Math.max(0, minBookings - bookings);
  const totalGoal = minConversions + minBookings;
  const totalDone = Math.min(conversions, minConversions) + Math.min(bookings, minBookings);
  const pct = Math.min(100, Math.round((totalDone / totalGoal) * 100));
  const currentEom = performance.current_eom;

  const encouragements = [
    'Every conversion counts — you’ve got this! 🎉',
    'Small wins stack up fast. Keep going! 🚀',
    'One more “yes” takes you closer to the trophy. 🏆',
    'Consistency beats luck. Today is a great day to convert! ✨',
  ];
  // Deterministic-per-month pick so the message doesn't flicker on every refresh.
  const monthKey = (performance.month || '').split('-').pop() || '0';
  const msg = encouragements[Number(monthKey) % encouragements.length];

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#E8E5DF] bg-gradient-to-r from-[#FFFDF8] via-white to-[#FFF4E6] p-4"
      data-testid="employee-progress-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FFD166]/30 flex items-center justify-center">
          <Target className="w-5 h-5 text-[#9C6B12]" />
        </div>
        <div className="flex-1 min-w-0">
          {currentEom ? (
            <p className="text-[11px] uppercase tracking-wide text-[#9C6B12] font-semibold flex items-center gap-1">
              <Trophy className="w-3 h-3" /> Employee of the Month: <span className="text-[#1E1E24]">{currentEom.name}</span>
            </p>
          ) : (
            <p className="text-[11px] uppercase tracking-wide text-[#6B6B7A] font-semibold">Your Progress this month</p>
          )}
          <p
            className="text-sm sm:text-base font-semibold text-[#1E1E24] mt-0.5"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
            data-testid="employee-progress-headline"
          >
            {conversionsNeeded === 0 && bookingsNeeded === 0
              ? "You've hit the targets — the award is being processed!"
              : `${conversionsNeeded} more conversion${conversionsNeeded === 1 ? '' : 's'} & ${bookingsNeeded} more repeat booking${bookingsNeeded === 1 ? '' : 's'} to take the crown 👑`}
          </p>
          <p className="text-xs text-[#6B6B7A] mt-0.5">{msg}</p>

          {/* Progress bar */}
          <div className="mt-2 h-2 w-full rounded-full bg-[#F3F0EA] overflow-hidden" aria-label="EOM progress">
            <div
              className="h-full bg-gradient-to-r from-[#FFD166] to-[#FF3B6B] transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="employee-progress-fill"
            />
          </div>
          <div className="flex items-center justify-between mt-1 text-[11px] text-[#6B6B7A]">
            <span data-testid="employee-progress-conversions">
              Conversions: <strong className="text-[#1E1E24]">{conversions}</strong> / {minConversions}
            </span>
            <span data-testid="employee-progress-bookings">
              Repeat bookings: <strong className="text-[#1E1E24]">{bookings}</strong> / {minBookings}
            </span>
            <span className="hidden sm:inline">Rating <strong className="text-[#1E1E24]">{performance.performance_rating}%</strong></span>
          </div>
        </div>
      </div>
    </div>
  );
};
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

export const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [clockedIn, setClockedIn] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const [timeLogs, setTimeLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState('00:00:00');
  const [shiftNotes, setShiftNotes] = useState('');
  const [shiftBookingId, setShiftBookingId] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [availOpen, setAvailOpen] = useState(false);
  const [reqAvailability, setReqAvailability] = useState([]);
  const [reqMessage, setReqMessage] = useState('');
  const [autoClockoutBanner, setAutoClockoutBanner] = useState(null);
  const [paystubs, setPaystubs] = useState([]);
  const [performance, setPerformance] = useState(null);

  // ── New: assigned parties + recent messages + view router state ──
  const [myParties, setMyParties] = useState([]);
  // Unread total for the internal Messages (TeamChat) sidebar badge
  const [teamUnread, setTeamUnread] = useState(0);
  // Customer party chats (host threads for this employee's assigned parties)
  const [custChats, setCustChats] = useState([]);
  const [custChatActive, setCustChatActive] = useState(null);
  const [custChatUnread, setCustChatUnread] = useState(0);
  const [custChatFilter, setCustChatFilter] = useState('all');
  const [swapDialogShift, setSwapDialogShift] = useState(null);
  const [swapsRefreshTrigger, setSwapsRefreshTrigger] = useState(0);
  const [pendingSwapsCount, setPendingSwapsCount] = useState(0);
  // Calendar is the default landing view (per the new portal spec). Other
  // sections (Schedule list, Parties list, Messages, Paystubs, Profile,
  // Recent Time Entries) only appear when the user picks them from the menu.
  const [activeView, setActiveView] = useState('calendar');
  // Mobile drawer toggle. Desktop sidebar is always visible above sm breakpoint.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const goToView = useCallback((key) => {
    setActiveView(key);
    setSidebarOpen(false); // auto-close mobile drawer after pick
  }, []);

  // Open a specific section when the header dropdown links to /employee#paystubs etc.
  const location = useLocation();
  useEffect(() => {
    const h = (location.hash || '').replace('#', '');
    const valid = ['calendar', 'schedule', 'parties', 'swaps', 'messages', 'customer_chats', 'paystubs', 'profile', 'time_logs'];
    if (valid.includes(h)) setActiveView(h);
  }, [location.hash]);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'employee')) {
      navigate('/login');
      return;
    }
    if (user?.role === 'employee') fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [statusRes, logsRes, shiftsRes, profileRes, bannerRes, paystubsRes, partiesRes, swapsRes, perfRes] = await Promise.all([
        axios.get(`${API_URL}/api/employee/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employee/time-logs?days=30`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employee/shifts?upcoming_only=true`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/employee/profile`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/employee/auto-clockout-banner`, { withCredentials: true }).catch(() => ({ data: { entry: null } })),
        axios.get(`${API_URL}/api/employee/paystubs`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/employee/my-parties?upcoming_only=false`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API_URL}/api/employee/shift-swaps`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/employee/performance`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setClockedIn(statusRes.data.clocked_in);
      setCurrentShift(statusRes.data.current_shift);
      setOnBreak(statusRes.data.current_shift?.on_break || false);
      setShiftNotes(statusRes.data.current_shift?.notes || '');
      setShiftBookingId(statusRes.data.current_shift?.booking_id || '');
      setTimeLogs(logsRes.data);
      setUpcomingShifts(shiftsRes.data || []);
      setProfile(profileRes.data);
      if (profileRes.data?.availability) setReqAvailability(profileRes.data.availability);
      setAutoClockoutBanner(bannerRes.data?.entry || null);
      setPaystubs(paystubsRes.data || []);
      setMyParties(partiesRes.data?.items || []);
      const pending = (swapsRes.data || []).filter((s) => s.status === 'pending').length;
      setPendingSwapsCount(pending);
      setPerformance(perfRes.data || null);
    } catch (e) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const dismissAutoClockout = async () => {
    try {
      await axios.post(`${API_URL}/api/employee/auto-clockout-banner/dismiss`, {}, { withCredentials: true });
      setAutoClockoutBanner(null);
    } catch { /* ignore */ }
  };

  // Live-refresh when admin assigns the employee to a new party (SSE bus
  // pushes `new_message` of kind=assignment from /admin/bookings/.../booked-by)
  useMessageStream(useCallback((evt) => {
    if (evt?.type === 'new_message' && evt?.kind === 'assignment') {
      toast.success(evt.subject || 'You\'ve been assigned a new party', {
        description: 'Check your dashboard for details.',
      });
      fetchData();
    }
    // Unified-calendar live updates — refetch on any booking/shift mutation
    // the server has broadcast to us (only affects our own data per role).
    if (evt?.type === 'calendar_changed') {
      fetchData();
    }
  }, []));

  const submitAvailabilityRequest = async () => {
    if (!reqAvailability.length) { toast.error('Select at least one day'); return; }
    try {
      await axios.post(`${API_URL}/api/employee/availability-request`,
        { requested_availability: reqAvailability, message: reqMessage },
        { withCredentials: true });
      toast.success('Request sent to admin');
      setAvailOpen(false);
      setReqMessage('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // Elapsed time counter (pause display during break)
  useEffect(() => {
    if (!clockedIn || !currentShift?.clock_in) return;
    const interval = setInterval(() => {
      const start = new Date(currentShift.clock_in);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      const h = String(Math.floor(diff / 3600)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const s = String(diff % 60).padStart(2, '0');
      setElapsed(`${h}:${m}:${s}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [clockedIn, currentShift]);

  const handleClockIn = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/employee/clock-in`, {
        notes: shiftNotes, booking_id: shiftBookingId || null
      }, { withCredentials: true });
      toast.success('Clocked in!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const handleClockOut = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/employee/clock-out`, {}, { withCredentials: true });
      toast.success(`Clocked out! Worked ${res.data.hours}h (${res.data.break_minutes}m break)`);
      setClockedIn(false); setCurrentShift(null); setOnBreak(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const handleBreakStart = async () => {
    try {
      await axios.post(`${API_URL}/api/employee/break/start`, {}, { withCredentials: true });
      setOnBreak(true);
      toast.success('Break started');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBreakEnd = async () => {
    try {
      await axios.post(`${API_URL}/api/employee/break/end`, {}, { withCredentials: true });
      setOnBreak(false);
      toast.success('Break ended');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveNotes = async () => {
    try {
      await axios.post(`${API_URL}/api/employee/update-notes`, {
        notes: shiftNotes, booking_id: shiftBookingId || null
      }, { withCredentials: true });
      toast.success('Notes saved');
      setNotesOpen(false);
    } catch (e) { toast.error('Failed'); }
  };

  // Customer party-chat threads: business threads (handled by all staff) + host
  // threads for this employee's assigned parties.
  const fetchCustChats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff/threads?scope=all`, { withCredentials: true });
      const threads = res.data.threads || [];
      setCustChats(threads);
      setCustChatUnread(threads.reduce((s, t) => s + (t.unread || 0), 0));
    } catch { /* noop */ }
  }, []);

  useEffect(() => { fetchCustChats(); }, [fetchCustChats]);

  useChatStream(useCallback((evt) => {
    if (evt?.type === 'thread_message' || evt?.type === 'thread_read') fetchCustChats();
  }, [fetchCustChats]));

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]"><Header />
        <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24 max-w-7xl">
        {/* Top bar — burger (mobile only) + title */}
        <div className="flex items-center gap-3 mb-4">
          <Button
            variant="outline"
            size="icon"
            className="lg:hidden rounded-full h-10 w-10 flex-shrink-0"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            data-testid="emp-menu-toggle"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-semibold text-[#1E1E24] truncate" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Employee Portal
            </h1>
            <p className="text-xs sm:text-sm text-[#6B6B7A] truncate">
              Welcome, {user?.name} ({user?.employee_role || 'staff'})
              {profile?.employee_number && (
                <span className="ml-2 font-mono text-[#1E1E24] font-semibold" data-testid="emp-portal-employee-number">
                  · {profile.employee_number}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Main 2-column grid: collapsible sidebar + main view */}
        <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-4 sm:gap-6">
          {/* Sidebar — always rendered. Hidden offscreen on mobile until burger opens it. */}
          <SidebarNav
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeView={activeView}
            onPick={goToView}
            badges={{
              parties: myParties.length || null,
              messages: teamUnread || null,
              customer_chats: custChatUnread || null,
              paystubs: paystubs.length || null,
              swaps: pendingSwapsCount || null,
            }}
          />

          <main className="min-w-0 space-y-4">
            {/* Employee of the Month banner — only renders for employees,
                only the actual awardee sees the gold "Congrats" banner. Every
                other employee sees a YOUR PROGRESS bar with what's left to hit
                the targets, alongside the current month's winner (if any). */}
            {user?.role === 'employee' && performance && (
              performance.employee_of_the_month === true ? (
                <div
                  className="relative overflow-hidden rounded-2xl border-2 border-[#FFD166] bg-gradient-to-r from-[#FFF8E7] via-[#FFFDF8] to-[#FFE9A8] p-4 flex items-start gap-3 animate-pulse"
                  style={{ animationDuration: '2.4s' }}
                  data-testid="employee-of-the-month-banner"
                >
                  <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#FFD166]/30 blur-2xl" aria-hidden />
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FFD166] flex items-center justify-center shadow-md">
                    <span aria-hidden style={{ fontSize: 22, lineHeight: 1 }}>🏆</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wide text-[#9C6B12] font-semibold">⭐ Employee of the Month</p>
                    <p className="text-base sm:text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Congrats {performance.name || user?.name}! You&apos;re crushing it this month.
                    </p>
                    <p className="text-xs text-[#6B6B7A]">
                      {performance.monthly_conversions} conversions · {performance.monthly_bookings} repeat bookings · {performance.performance_rating}% rating
                    </p>
                  </div>
                </div>
              ) : (
                <EmployeeProgressBanner performance={performance} />
              )
            )}

            {/* Auto clock-out banner */}
            {autoClockoutBanner && (
              <div
                className="rounded-2xl border-2 border-[#FFD166] bg-[#FFF8E7] p-4 flex items-start gap-3"
                data-testid="auto-clockout-banner"
              >
                <Clock className="w-5 h-5 text-[#B8860B] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#1E1E24] text-sm">You were auto-clocked out at 7:00 PM</p>
                  <p className="text-xs text-[#6B6B7A] mt-1">
                    Total hours recorded: <strong>{autoClockoutBanner.hours || 0}h</strong>. Contact your manager to adjust.
                  </p>
                </div>
                <button onClick={dismissAutoClockout} className="text-xs text-[#B8860B] hover:underline font-medium" data-testid="dismiss-auto-clockout-banner">
                  Got it
                </button>
              </div>
            )}

            {/* Compact Time Clock Card — always visible above the main view. */}
            <CompactTimeClockCard
              clockedIn={clockedIn}
              onBreak={onBreak}
              currentShift={currentShift}
              elapsed={elapsed}
              processing={processing}
              onClockIn={handleClockIn}
              onClockOut={handleClockOut}
              onBreakStart={handleBreakStart}
              onBreakEnd={handleBreakEnd}
              onNotes={() => setNotesOpen(true)}
            />

            {/* === MAIN VIEW (one section at a time) === */}
            {activeView === 'calendar' && (
              <div data-testid="emp-view-calendar" className="space-y-4">
                <EmployeeCalendarView shifts={upcomingShifts} parties={myParties} large />
              </div>
            )}

            {activeView === 'schedule' && (
              <SectionCard title="My Schedule" subtitle="Your upcoming shifts (read-only)" icon={Calendar} accent="#118AB2" testid="emp-view-schedule">
                {upcomingShifts.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-[#6B6B7A]">No shifts scheduled yet — your manager will publish them soon.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Start</TableHead>
                          <TableHead>End</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead className="hidden sm:table-cell">Role</TableHead>
                          <TableHead className="hidden md:table-cell">Notes</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {upcomingShifts.slice(0, 30).map((s) => {
                          const [sh, sm] = (s.start_time || '0:0').split(':').map(Number);
                          const [eh, em] = (s.end_time || '0:0').split(':').map(Number);
                          const hours = (((eh * 60 + em) - (sh * 60 + sm)) / 60).toFixed(2);
                          return (
                            <TableRow key={s.shift_id}>
                              <TableCell className="font-medium whitespace-nowrap">{new Date(s.date + 'T00:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}</TableCell>
                              <TableCell className="font-mono">{s.start_time}</TableCell>
                              <TableCell className="font-mono">{s.end_time}</TableCell>
                              <TableCell className="font-semibold">{hours}h</TableCell>
                              <TableCell className="text-xs capitalize hidden sm:table-cell">{(s.role || 'staff').replace('_', ' ')}</TableCell>
                              <TableCell className="text-xs text-[#6B6B7A] max-w-[200px] truncate hidden md:table-cell">{s.notes || '-'}</TableCell>
                              <TableCell className="text-right">
                                <button
                                  type="button"
                                  onClick={() => setSwapDialogShift(s)}
                                  className="text-xs text-[#FF3B6B] hover:underline whitespace-nowrap"
                                  data-testid={`request-swap-${s.shift_id}`}
                                >
                                  <ArrowLeftRight className="w-3 h-3 inline mr-1" />Request swap
                                </button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </SectionCard>
            )}

            {activeView === 'swaps' && (
              <SectionCard title="Shift Swaps" subtitle="Your pending and past swap requests" icon={ArrowLeftRight} accent="#FF3B6B" testid="emp-view-swaps">
                <div className="p-4">
                  <MyShiftSwapsList refreshTrigger={swapsRefreshTrigger} />
                </div>
              </SectionCard>
            )}

            {activeView === 'parties' && (
              <SectionCard title="My Parties" subtitle="Bookings you'll be hosting" icon={PartyPopper} accent="#FF3B6B" testid="emp-view-parties">
                {myParties.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-[#6B6B7A]">You haven't been assigned to any parties yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Child</TableHead>
                          <TableHead className="hidden sm:table-cell">Package</TableHead>
                          <TableHead className="hidden sm:table-cell">Guests</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {myParties.map((p) => (
                          <TableRow key={p.booking_id} data-testid={`emp-party-row-${p.booking_id}`}>
                            <TableCell className="font-medium whitespace-nowrap">{new Date(p.date + 'T00:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}</TableCell>
                            <TableCell className="font-mono text-xs">{p.time_slot}</TableCell>
                            <TableCell>{p.child_name || '—'}{p.child_age ? <span className="text-[#6B6B7A] text-xs ml-1">({p.child_age}y)</span> : ''}</TableCell>
                            <TableCell className="text-xs hidden sm:table-cell">{p.package_name || '—'}</TableCell>
                            <TableCell className="text-xs hidden sm:table-cell">{p.guest_count || '—'}</TableCell>
                            <TableCell>
                              {p.party_completed ? (
                                <Badge className="bg-[#9333EA]/15 text-[#9333EA] border-none text-[10px]">Done</Badge>
                              ) : p.arrived ? (
                                <Badge className="bg-[#06D6A0]/15 text-[#06D6A0] border-none text-[10px]">Arrived</Badge>
                              ) : (
                                <Badge className="bg-[#FFD166]/20 text-[#B8860B] border-none text-[10px]">Upcoming</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </SectionCard>
            )}

            {activeView === 'messages' && (
              <div data-testid="emp-view-messages">
                <TeamChat
                  mode="employee"
                  title="Messages"
                  subtitle="Chat with your managers and the office"
                  onUnread={setTeamUnread}
                />
              </div>
            )}

            {activeView === 'customer_chats' && (
              <SectionCard title="Customer Chats" subtitle="Business inquiries (shared) and the families for your assigned parties" icon={MessagesSquare} accent="#FF3B6B" testid="emp-view-customer-chats">
                <div className="px-4 pt-3 flex items-center gap-2">
                  {[{ k: 'all', l: 'All' }, { k: 'business', l: 'Business' }, { k: 'host', l: 'My Parties' }].map((f) => (
                    <button
                      key={f.k}
                      onClick={() => { setCustChatFilter(f.k); setCustChatActive(null); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${custChatFilter === f.k ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'}`}
                      data-testid={`emp-cchat-filter-${f.k}`}
                    >
                      {f.l}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-[58vh] mt-3 border-t border-[#F0EDE6]">
                  <div className={`md:border-r border-[#F0EDE6] overflow-y-auto ${custChatActive ? 'hidden md:block' : 'block'}`}>
                    <ChatThreadList
                      threads={custChats.filter((t) => custChatFilter === 'all' ? true : t.audience === custChatFilter)}
                      activeThreadId={custChatActive}
                      onSelect={(t) => setCustChatActive(t.thread_id)}
                      emptyText="No chats here yet. Business inquiries and your party families appear here."
                    />
                  </div>
                  <div className={`${custChatActive ? 'block' : 'hidden md:block'} min-h-0`}>
                    {custChatActive ? (
                      <ChatConversation apiBase="staff" threadId={custChatActive} onBack={() => setCustChatActive(null)} onActivity={fetchCustChats} />
                    ) : (
                      <div className="h-full flex items-center justify-center text-center p-6 text-sm text-[#6B6B7A]">
                        Select a chat to view and reply.
                      </div>
                    )}
                  </div>
                </div>
              </SectionCard>
            )}

            {activeView === 'paystubs' && (
              <SectionCard title="My Paystubs" subtitle="Click to download as PDF" icon={DollarSign} accent="#06D6A0" testid="emp-view-paystubs">
                {paystubs.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-[#6B6B7A]">No paystubs yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Period</TableHead>
                          <TableHead className="hidden sm:table-cell">Type</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead className="hidden sm:table-cell">Gross</TableHead>
                          <TableHead>Net</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paystubs.map((ps) => (
                          <TableRow key={ps.paystub_id} data-testid={`my-paystub-${ps.paystub_id}`}>
                            <TableCell className="text-xs whitespace-nowrap">{ps.pay_period_start} → {ps.pay_period_end}</TableCell>
                            <TableCell className="hidden sm:table-cell"><Badge className="bg-[#FFD166]/20 text-[#B8860B] border-none">{ps.period_type}</Badge></TableCell>
                            <TableCell>{ps.total_hours}h</TableCell>
                            <TableCell className="hidden sm:table-cell">${ps.gross_pay?.toFixed(2)}</TableCell>
                            <TableCell className="font-bold text-[#FF3B6B]">${ps.net_pay?.toFixed(2)}</TableCell>
                            <TableCell>
                              <Badge className={ps.status === 'viewed' ? 'bg-[#06D6A0]/20 text-[#06D6A0] border-none text-[10px]' : 'bg-gray-100 text-gray-700 border-none text-[10px]'}>
                                {ps.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => window.open(`${API_URL}/api/employee/paystubs/${ps.paystub_id}/pdf`, '_blank')} className="h-7 text-xs rounded-full" data-testid={`download-my-paystub-${ps.paystub_id}`}>
                                <Download className="w-3 h-3 mr-1" /> PDF
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </SectionCard>
            )}

            {activeView === 'profile' && profile && (
              <SectionCard
                title="My Profile"
                subtitle="Your employment details"
                icon={UserIcon}
                accent="#FF3B6B"
                testid="emp-view-profile"
                headerExtra={
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setReqAvailability(profile.availability || []);
                      setAvailOpen(true);
                    }}
                    className="rounded-full border-[#E8E5DF] text-xs"
                    data-testid="request-avail-btn"
                  >
                    <CalendarDays className="w-3 h-3 mr-1" /> Request Availability Change
                  </Button>
                }
              >
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-[#6B6B7A]">Employee Number</p><p className="font-mono font-semibold text-[#1E1E24]" data-testid="emp-profile-employee-number">{profile.employee_number || '—'}</p></div>
                  <div><p className="text-xs text-[#6B6B7A]">Employment Type</p><p className="font-medium capitalize">{EMPLOYMENT_LABELS[profile.employment_type] || '—'}</p></div>
                  <div><p className="text-xs text-[#6B6B7A]">Role</p><p className="font-medium capitalize">{(profile.employee_role || '').replace('_', ' ')}</p></div>
                  <div><p className="text-xs text-[#6B6B7A]">Start Date</p><p className="font-medium">{profile.start_date || '—'}{profile.tenure_months != null && <span className="ml-2 text-xs text-[#06D6A0]">({profile.tenure_months >= 12 ? `${(profile.tenure_months/12).toFixed(1)} yrs` : `${profile.tenure_months} mo`})</span>}</p></div>
                  <div><p className="text-xs text-[#6B6B7A]">Date of Birth / Age</p><p className="font-medium">{profile.date_of_birth || '—'}{profile.age != null && <span className="ml-2 text-xs text-[#6B6B7A]">(Age {profile.age})</span>}</p></div>
                  {profile.address && (
                    <div className="sm:col-span-2"><p className="text-xs text-[#6B6B7A] flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</p><p className="font-medium">{profile.address.street}, {profile.address.city}, {profile.address.province} {profile.address.postal_code}</p></div>
                  )}
                  <div className="sm:col-span-2">
                    <p className="text-xs text-[#6B6B7A] mb-1">Days Available</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(profile.availability || []).length === 0 && <span className="text-[#6B6B7A] text-xs">—</span>}
                      {(profile.availability || []).map((d) => (
                        <span key={d} className="px-2 py-0.5 bg-[#06D6A0]/10 text-[#06D6A0] rounded text-xs">{DAY_SHORT[d]}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}

            {activeView === 'time_logs' && (
              <SectionCard title="Time Logs" subtitle="Your last 30 days of clock-ins" icon={Timer} accent="#6B6B7A" testid="emp-view-time-logs">
                {/* Stats */}
                {timeLogs && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 pt-4">
                    <StatCell color="#FF3B6B" Icon={Timer} value={`${timeLogs.total_hours}h`} label="Total (30d)" />
                    <StatCell color="#06D6A0" Icon={Calendar} value={`${timeLogs.entries?.length || 0}`} label="Shifts" />
                    <StatCell color="#B8860B" Icon={DollarSign} value={`${timeLogs.weekly_summary?.[0]?.total_hours || 0}h`} label="This week" />
                    <StatCell color="#dc2626" Icon={AlertTriangle} value={`${timeLogs.overtime_days?.length || 0}`} label="OT days" />
                  </div>
                )}
                {(timeLogs?.overtime_days?.length > 0 || timeLogs?.overtime_weeks?.length > 0) && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mx-4 mt-3 text-xs text-red-700 space-y-1">
                    <p className="font-semibold">Overtime alerts</p>
                    {timeLogs.overtime_days?.map((d, i) => <p key={`d${i}`}>{d.date}: {d.hours}h (&gt;8h daily)</p>)}
                    {timeLogs.overtime_weeks?.map((w, i) => <p key={`w${i}`}>{w.week}: {w.hours}h (&gt;40h weekly)</p>)}
                  </div>
                )}
                <div className="overflow-x-auto p-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>In</TableHead>
                        <TableHead>Out</TableHead>
                        <TableHead className="hidden sm:table-cell">Break</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead className="hidden md:table-cell">Notes</TableHead>
                        <TableHead className="hidden md:table-cell">Booking</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(!timeLogs?.entries || timeLogs.entries.length === 0) ? (
                        <TableRow><TableCell colSpan={7} className="text-center py-6 text-[#6B6B7A]">No entries yet</TableCell></TableRow>
                      ) : timeLogs.entries.slice(0, 30).map((e) => (
                        <TableRow key={e.entry_id} className={e.flagged ? 'bg-red-50' : ''}>
                          <TableCell className="font-medium whitespace-nowrap">{e.date}</TableCell>
                          <TableCell>{e.clock_in ? new Date(e.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                          <TableCell>{e.clock_out ? new Date(e.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : <span className="text-[#06D6A0] font-medium">Active</span>}</TableCell>
                          <TableCell className="hidden sm:table-cell">{e.break_minutes ? `${e.break_minutes}m` : '-'}</TableCell>
                          <TableCell className="font-semibold">{e.hours ? `${e.hours}h` : '-'}</TableCell>
                          <TableCell className="max-w-[140px] truncate text-xs text-[#6B6B7A] hidden md:table-cell">{e.notes || '-'}</TableCell>
                          <TableCell className="text-xs font-mono hidden md:table-cell">{e.booking_id || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </SectionCard>
            )}
          </main>
        </div>
      </div>


      {/* Notes Dialog */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Shift Notes</DialogTitle>
            <DialogDescription className="text-xs text-[#6B6B7A]">Add a note to your active shift (optional).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Notes</Label>
              <Textarea value={shiftNotes} onChange={e => setShiftNotes(e.target.value)} rows={4}
                placeholder="e.g., Setup for birthday party, helped with cleanup..." className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="shift-notes" />
            </div>
            <div>
              <Label className="font-semibold">Link to Booking (optional)</Label>
              <Input value={shiftBookingId} onChange={e => setShiftBookingId(e.target.value)}
                placeholder="e.g., booking_abc123" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="shift-booking-id" />
            </div>
            <Button onClick={saveNotes} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="save-shift-notes">Save Notes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Shift Swap Dialog */}
      <RequestSwapDialog
        open={!!swapDialogShift}
        onOpenChange={(o) => !o && setSwapDialogShift(null)}
        shift={swapDialogShift}
        onCreated={() => { setSwapsRefreshTrigger((n) => n + 1); fetchData(); }}
      />

      {/* Availability Request Dialog */}
      <Dialog open={availOpen} onOpenChange={setAvailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Request Availability Change</DialogTitle>
            <DialogDescription className="text-xs text-[#6B6B7A]">Pick the days you can work and your manager will review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#6B6B7A]">Select the days you'd like to be available. Your admin will review & confirm.</p>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(d => {
                const selected = reqAvailability.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setReqAvailability(selected ? reqAvailability.filter(x => x !== d) : [...reqAvailability, d])}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selected ? 'bg-[#06D6A0] text-white border-[#06D6A0]' : 'bg-white text-[#6B6B7A] border-[#E8E5DF]'
                    }`}
                    data-testid={`req-day-${d}`}
                  >
                    {DAY_SHORT[d]}
                  </button>
                );
              })}
            </div>
            <div>
              <Label className="font-semibold">Message to admin (optional)</Label>
              <Textarea value={reqMessage} onChange={e => setReqMessage(e.target.value)} rows={3} placeholder="e.g., Starting school, need evenings free..." className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="req-message" />
            </div>
            <Button onClick={submitAvailabilityRequest} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="submit-avail-request">Send Request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDashboard;

// ─── Sidebar nav (drawer on mobile, fixed column on desktop) ───────
const NAV_ITEMS = [
  { key: 'calendar',  label: 'Calendar',     icon: CalendarDays, badgeKey: null },
  { key: 'schedule',  label: 'My Schedule',  icon: Calendar,     badgeKey: null },
  { key: 'parties',   label: 'My Parties',   icon: PartyPopper,  badgeKey: 'parties' },
  { key: 'swaps',     label: 'Shift Swaps',  icon: ArrowLeftRight, badgeKey: 'swaps' },
  { key: 'messages',  label: 'Messages',     icon: Inbox,        badgeKey: 'messages' },
  { key: 'customer_chats', label: 'Customer Chats', icon: MessagesSquare, badgeKey: 'customer_chats' },
  { key: 'paystubs',  label: 'Paystubs',     icon: DollarSign,   badgeKey: 'paystubs' },
  { key: 'profile',   label: 'My Profile',   icon: UserIcon,     badgeKey: null },
  { key: 'time_logs', label: 'Time Logs',    icon: Timer,        badgeKey: null },
];

const SidebarNav = ({ open, onClose, activeView, onPick, badges = {} }) => {
  const links = (
    <nav className="space-y-1" data-testid="emp-sidebar">
      {NAV_ITEMS.map((it) => {
        const isActive = activeView === it.key;
        const Icon = it.icon;
        const badge = it.badgeKey ? badges[it.badgeKey] : null;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onPick(it.key)}
            data-testid={`emp-nav-${it.key}`}
            aria-current={isActive ? 'page' : undefined}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive
                ? 'bg-[#FF3B6B]/10 text-[#FF3B6B]'
                : 'text-[#1E1E24] hover:bg-[#FFFDF8] hover:text-[#FF3B6B]'
            }`}
          >
            <Icon className={`w-4 h-4 ${isActive ? 'text-[#FF3B6B]' : 'text-[#6B6B7A]'}`} />
            <span className="flex-1 text-left">{it.label}</span>
            {badge ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF3B6B] text-white min-w-[20px] text-center">
                {badge > 99 ? '99+' : badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
  return (
    <>
      {/* Desktop column */}
      <aside className="hidden lg:block sticky top-4 self-start">
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-2">{links}</div>
      </aside>
      {/* Mobile drawer + backdrop */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/40 z-40"
              onClick={onClose}
              data-testid="emp-sidebar-backdrop"
            />
            <motion.aside
              key="drawer"
              initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
              transition={{ type: 'tween', duration: 0.22 }}
              className="lg:hidden fixed top-0 left-0 bottom-0 w-72 max-w-[80vw] bg-white z-50 shadow-2xl p-3 overflow-y-auto"
              data-testid="emp-sidebar-drawer"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Menu</p>
                <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full" aria-label="Close menu" data-testid="emp-sidebar-close">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              {links}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// ─── Compact horizontal time clock card ───────────────────────────
const CompactTimeClockCard = ({
  clockedIn, onBreak, currentShift, elapsed, processing,
  onClockIn, onClockOut, onBreakStart, onBreakEnd, onNotes,
}) => {
  const stateLabel = onBreak ? 'On Break' : clockedIn ? 'Working' : 'Not clocked in';
  const stateColor = onBreak ? '#B8860B' : clockedIn ? '#06D6A0' : '#6B6B7A';
  const stateBg = onBreak ? '#FFD166' : clockedIn ? '#06D6A0' : '#E8E5DF';
  const Icon = onBreak ? Coffee : Clock;
  return (
    <div
      className={`rounded-2xl border-2 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
        onBreak ? 'bg-[#FFD166]/5 border-[#FFD166]' : clockedIn ? 'bg-[#06D6A0]/5 border-[#06D6A0]' : 'bg-white border-[#E8E5DF]'
      }`}
      data-testid="emp-time-clock-card"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: `${stateBg}33` }}
        >
          <Icon className="w-5 h-5" style={{ color: stateColor }} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium" style={{ color: stateColor }}>{stateLabel}</p>
          {clockedIn ? (
            <>
              <p className="text-2xl sm:text-3xl font-bold text-[#1E1E24] leading-tight" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="elapsed-time">
                {elapsed}
              </p>
              <p className="text-[11px] text-[#6B6B7A] truncate">
                In at {currentShift?.clock_in ? new Date(currentShift.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                {currentShift?.break_minutes > 0 && ` · break ${currentShift.break_minutes}m`}
                {currentShift?.booking_id && (
                  <span className="ml-1 text-[#FF3B6B] inline-flex items-center gap-0.5">
                    <Link2 className="w-2.5 h-2.5" /> {currentShift.booking_id}
                  </span>
                )}
              </p>
            </>
          ) : (
            <p className="text-sm font-semibold text-[#1E1E24]">Tap "Clock In" to start</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
        {clockedIn ? (
          <>
            {onBreak ? (
              <Button onClick={onBreakEnd} className="bg-[#FFD166] hover:bg-[#E6BD57] text-white rounded-full h-9 px-4 text-xs sm:text-sm flex-1 sm:flex-initial" data-testid="end-break-btn">
                <Coffee className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">End Break</span>
              </Button>
            ) : (
              <Button onClick={onBreakStart} variant="outline" className="rounded-full h-9 px-4 text-xs sm:text-sm border-[#FFD166] text-[#B8860B] flex-1 sm:flex-initial" data-testid="start-break-btn">
                <Coffee className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Break</span>
              </Button>
            )}
            <Button onClick={onNotes} variant="outline" className="rounded-full h-9 px-4 text-xs sm:text-sm border-[#E8E5DF] flex-1 sm:flex-initial" data-testid="notes-btn">
              <FileText className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Notes</span>
            </Button>
            <Button onClick={onClockOut} disabled={processing} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full h-9 px-4 text-xs sm:text-sm flex-1 sm:flex-initial" data-testid="clock-out-btn">
              <LogOut className="w-4 h-4 sm:mr-1.5" />
              {processing ? '…' : 'Clock Out'}
            </Button>
          </>
        ) : (
          <Button onClick={onClockIn} disabled={processing} className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full h-10 px-5 text-sm w-full sm:w-auto" data-testid="clock-in-btn">
            <LogIn className="w-4 h-4 mr-1.5" />
            {processing ? 'Processing…' : 'Clock In'}
          </Button>
        )}
      </div>
    </div>
  );
};

// ─── Lightweight section card (single-view rendering) ─────────────
const SectionCard = ({ title, subtitle, icon: Icon, accent = '#FF3B6B', headerExtra, children, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid={testid}>
    <div className="px-4 py-3 flex items-center gap-3 border-b border-[#F0EDE6] flex-wrap">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${accent}18` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{title}</h3>
        {subtitle && <p className="text-xs text-[#6B6B7A]">{subtitle}</p>}
      </div>
      {headerExtra}
    </div>
    {children}
  </div>
);

// ─── Tiny stat cell used in Time Logs view ────────────────────────
const StatCell = ({ color, Icon, value, label }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-3 sm:p-4">
    <div className="flex items-center gap-2 sm:gap-3">
      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}1A` }}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-base sm:text-lg font-bold text-[#1E1E24] truncate" style={{ fontFamily: 'Fredoka, sans-serif' }}>{value}</p>
        <p className="text-[10px] sm:text-xs text-[#6B6B7A] truncate">{label}</p>
      </div>
    </div>
  </div>
);
